import { useReducer, useEffect, useRef } from "react";
import { fetchChargerEvents } from "../../events/hooks/api";

const ACTIONS = {
  FETCH_INIT: "FETCH_EVENTS_INIT",
  FETCH_SUCCESS: "FETCH_EVENTS_SUCCESS",
  FETCH_FAILURE: "FETCH_EVENTS_FAILURE",
};

const initialState = {
  data: [],
  isLoading: false,
  error: null,
};

function eventsReducer(state, action) {
  switch (action.type) {
    case ACTIONS.FETCH_INIT:
      return { ...state, isLoading: true, error: null };
    case ACTIONS.FETCH_SUCCESS:
      return { ...state, isLoading: false, data: action.payload };
    case ACTIONS.FETCH_FAILURE:
      return { ...state, isLoading: false, error: action.payload || "Failed to fetch events" };
    default:
      return state;
  }
}

const FETCH_DEBOUNCE_MS = 1000;

export function useEventsReducer({ chargerId, timeRange, siteId }) {
  const [state, dispatch] = useReducer(eventsReducer, initialState);
  const lastFetchRef = useRef(null);

  useEffect(() => {
    if (!chargerId || !siteId || !timeRange?.[0] || !timeRange?.[1]) return;

    const startIso = new Date(timeRange[0]).toISOString();
    const endIso = new Date(timeRange[1]).toISOString();
    const fetchKey = `${siteId}_${chargerId}_${startIso}_${endIso}`;

    const handler = setTimeout(() => {
      if (lastFetchRef.current === fetchKey) return;
      lastFetchRef.current = fetchKey;
      fetchEvents(dispatch, chargerId, siteId, timeRange);
    }, FETCH_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [chargerId, siteId, timeRange]);

  return { events: state };
}

async function fetchEvents(dispatch, chargerId, siteId, timeRange) {
  dispatch({ type: ACTIONS.FETCH_INIT });
  try {
    const [start, end] = timeRange;
    const startDate = start instanceof Date ? start.toISOString() : new Date(start).toISOString();
    const endDate = end instanceof Date ? end.toISOString() : new Date(end).toISOString();

    const res = await fetchChargerEvents(chargerId, {
      siteId,
      startDate,
      endDate,
    });

    const events = res?.events || [];
    dispatch({ type: ACTIONS.FETCH_SUCCESS, payload: events });
  } catch (error) {
    dispatch({ type: ACTIONS.FETCH_FAILURE, payload: error?.message });
  }
}

export default useEventsReducer;
